/**
 * Marketplace export modal: Facebook copy kit + eBay draft/publish actions
 * for won/listed lots.
 */
import {
  buildLotFacebookCopy,
  buildPhotoSheetHtml,
  copyTextToClipboard,
  downloadTextFile,
  lotFileSlug,
} from '../../modules/marketplaceExport';
import {
  fetchEbayStatus,
  startEbayConnect,
  exportLotToEbayRemote,
  publishEbayListingRemote,
  MarketplaceApiError,
  type EbayStatus,
} from '../../modules/apiOperations/marketplace';
import { showToast } from '../../modules/ui/toasts';
import type { Lot } from '../../types';
import type { TableHandlerDeps } from './types';

interface ExportModalElements {
  modal: HTMLElement;
  backdrop: HTMLElement;
  title: HTMLElement | null;
  preview: HTMLTextAreaElement | null;
  copyFacebook: HTMLButtonElement | null;
  photoSheet: HTMLButtonElement | null;
  ebayConnect: HTMLButtonElement | null;
  ebayDraft: HTMLButtonElement | null;
  ebayPublish: HTMLButtonElement | null;
  ebayView: HTMLAnchorElement | null;
  ebayStatus: HTMLElement | null;
}

function getExportModalElements(): ExportModalElements | null {
  const modal = document.getElementById('export-modal');
  const backdrop = document.getElementById('export-modal-backdrop');
  if (!modal || !backdrop) return null;
  return {
    modal,
    backdrop,
    title: document.getElementById('export-modal-title'),
    preview: document.getElementById('export-preview') as HTMLTextAreaElement | null,
    copyFacebook: document.getElementById('export-copy-facebook') as HTMLButtonElement | null,
    photoSheet: document.getElementById('export-photo-sheet') as HTMLButtonElement | null,
    ebayConnect: document.getElementById('export-ebay-connect') as HTMLButtonElement | null,
    ebayDraft: document.getElementById('export-ebay-draft') as HTMLButtonElement | null,
    ebayPublish: document.getElementById('export-ebay-publish') as HTMLButtonElement | null,
    ebayView: document.getElementById('export-ebay-view') as HTMLAnchorElement | null,
    ebayStatus: document.getElementById('export-ebay-status'),
  };
}

function findLot(deps: TableHandlerDeps, id: string): Lot | undefined {
  return deps.state.lots.find(lot => lot && lot.id === id);
}

function currentLot(deps: TableHandlerDeps): Lot | undefined {
  const id = deps.state.exportEdit?.id;
  return id ? findLot(deps, id) : undefined;
}

function closeExportModal(deps: TableHandlerDeps): void {
  const els = getExportModalElements();
  if (!els) return;
  els.modal.setAttribute('hidden', '');
  els.backdrop.setAttribute('hidden', '');
  document.body?.classList.remove('modal-open');
  deps.state.exportEdit = null;
}

/** Reflect connection/export state into the eBay action buttons. */
function renderEbaySection(deps: TableHandlerDeps, status: EbayStatus | null): void {
  const els = getExportModalElements();
  const lot = currentLot(deps);
  if (!els || !lot) return;

  const record = lot.marketplaceExports?.ebay;
  const show = (el: HTMLElement | null, visible: boolean) => {
    if (!el) return;
    if (visible) el.removeAttribute('hidden');
    else el.setAttribute('hidden', '');
  };

  if (!status || !status.configured) {
    show(els.ebayConnect, false);
    show(els.ebayDraft, false);
    show(els.ebayPublish, false);
    show(els.ebayView, false);
    if (els.ebayStatus) {
      els.ebayStatus.textContent = status
        ? 'eBay export is not enabled on this server yet.'
        : 'Could not reach the eBay service.';
    }
    return;
  }

  if (!status.connected) {
    show(els.ebayConnect, true);
    show(els.ebayDraft, false);
    show(els.ebayPublish, false);
    show(els.ebayView, false);
    if (els.ebayStatus) {
      els.ebayStatus.textContent =
        'Connect your eBay seller account to create draft listings from LotLingo.';
    }
    return;
  }

  show(els.ebayConnect, false);
  show(els.ebayDraft, true);
  show(els.ebayPublish, !!record?.offerId && !record.listingId);
  if (els.ebayView && record?.listingId) {
    const base =
      record.environment === 'sandbox' ? 'https://sandbox.ebay.com' : 'https://www.ebay.com';
    els.ebayView.href = `${base}/itm/${encodeURIComponent(record.listingId)}`;
    show(els.ebayView, true);
  } else {
    show(els.ebayView, false);
  }
  if (els.ebayDraft) {
    els.ebayDraft.textContent = record?.offerId ? 'Update eBay draft' : 'Create eBay draft';
  }
  if (els.ebayStatus) {
    els.ebayStatus.textContent = record?.listingId
      ? 'Published on eBay.'
      : record?.offerId
        ? 'Draft created on eBay — publish it from here or finish it in Seller Hub.'
        : `Connected to eBay (${status.environment ?? 'production'}).`;
  }
}

async function refreshEbaySection(deps: TableHandlerDeps): Promise<void> {
  let status: EbayStatus | null = null;
  try {
    status = await fetchEbayStatus(deps.state, deps.ensureAccessToken);
  } catch {
    status = null;
  }
  renderEbaySection(deps, status);
}

export function openExportModal(deps: TableHandlerDeps, lot: Lot): void {
  const els = getExportModalElements();
  if (!els) return;

  deps.state.exportEdit = { id: lot.id };
  if (els.preview) els.preview.value = buildLotFacebookCopy(lot);
  if (els.ebayStatus) els.ebayStatus.textContent = 'Checking eBay status…';

  els.modal.removeAttribute('hidden');
  els.backdrop.removeAttribute('hidden');
  document.body?.classList.add('modal-open');

  if (deps.state.mode === 'auth0' && lot.remote) {
    void refreshEbaySection(deps);
  } else {
    renderEbaySection(deps, { configured: false, connected: false });
    const statusEl = getExportModalElements()?.ebayStatus;
    if (statusEl) statusEl.textContent = 'Sign in to export to eBay.';
  }
}

async function withBusy(
  button: HTMLButtonElement | null,
  work: () => Promise<void>
): Promise<void> {
  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }
  try {
    await work();
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

function describeError(err: unknown): string {
  if (err instanceof MarketplaceApiError) {
    if (err.code === 'not-connected' || err.code === 'refresh-failed') {
      return 'Your eBay connection needs attention — please reconnect your account.';
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong.';
}

/** Wire the export modal controls once at boot. */
export function setupExportModalHandlers(deps: TableHandlerDeps): void {
  const els = getExportModalElements();
  if (!els) return;

  document
    .getElementById('close-export-modal')
    ?.addEventListener('click', () => closeExportModal(deps));
  els.backdrop.addEventListener('click', () => closeExportModal(deps));

  els.copyFacebook?.addEventListener('click', async () => {
    const lot = currentLot(deps);
    if (!lot) return;
    const ok = await copyTextToClipboard(buildLotFacebookCopy(lot));
    showToast(ok ? 'Listing text copied — paste it into Facebook Marketplace.' : 'Copy failed.', {
      variant: ok ? 'success' : 'error',
      duration: 3000,
    });
  });

  els.photoSheet?.addEventListener('click', () => {
    const lot = currentLot(deps);
    if (!lot) return;
    downloadTextFile(buildPhotoSheetHtml(lot), `${lotFileSlug(lot)}-photos.html`, 'text/html');
  });

  els.ebayConnect?.addEventListener('click', () =>
    withBusy(els.ebayConnect, async () => {
      try {
        const url = await startEbayConnect(deps.state, deps.ensureAccessToken);
        window.open(url, '_blank', 'noopener');
        showToast('Finish connecting on eBay, then come back and try the draft again.', {
          variant: 'info',
          duration: 5000,
        });
      } catch (err) {
        showToast(describeError(err), { variant: 'error', duration: 4000 });
      }
    })
  );

  els.ebayDraft?.addEventListener('click', () =>
    withBusy(els.ebayDraft, async () => {
      const lot = currentLot(deps);
      if (!lot) return;
      try {
        const result = await exportLotToEbayRemote(lot.id, deps.state, deps.ensureAccessToken);
        showToast(
          `eBay draft ready: “${result.title}” at $${result.price.toFixed(2)} with ${result.imageCount} photo${result.imageCount === 1 ? '' : 's'}.`,
          { variant: 'success', duration: 5000 }
        );
        // Reflect the new export record locally without waiting for a poll.
        lot.marketplaceExports = {
          ...(lot.marketplaceExports ?? {}),
          ebay: {
            sku: result.sku,
            offerId: result.offerId,
            exportedAt: new Date().toISOString(),
            marketplaceId: 'EBAY_US',
            environment: 'production',
            ...(lot.marketplaceExports?.ebay?.listingId
              ? { listingId: lot.marketplaceExports.ebay.listingId }
              : {}),
          },
        };
        await refreshEbaySection(deps);
      } catch (err) {
        showToast(describeError(err), { variant: 'error', duration: 5000 });
        if (
          err instanceof MarketplaceApiError &&
          (err.code === 'not-connected' || err.code === 'refresh-failed')
        ) {
          await refreshEbaySection(deps);
        }
      }
    })
  );

  els.ebayPublish?.addEventListener('click', () =>
    withBusy(els.ebayPublish, async () => {
      const lot = currentLot(deps);
      if (!lot) return;
      try {
        const result = await publishEbayListingRemote(lot.id, deps.state, deps.ensureAccessToken);
        if (lot.marketplaceExports?.ebay) {
          lot.marketplaceExports.ebay.listingId = result.listingId;
          lot.marketplaceExports.ebay.publishedAt = new Date().toISOString();
        }
        // The server already marked the lot listed; mirror locally.
        const status = lot.lifecycleStatus;
        if (!status || status === 'watching' || status === 'bidding' || status === 'won') {
          lot.lifecycleStatus = 'listed';
        }
        deps.render();
        showToast('Listing published on eBay.', { variant: 'success', duration: 4000 });
        await refreshEbaySection(deps);
      } catch (err) {
        showToast(describeError(err), { variant: 'error', duration: 6000 });
      }
    })
  );
}

// ── Row action ─────────────────────────────────────────────────────────

export function handleExportLot(id: string, e: Event, deps: TableHandlerDeps): void {
  e.preventDefault();
  const lot = findLot(deps, id);
  if (lot) openExportModal(deps, lot);
}
